import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import Avatar from '@liquidweb-dev/undercurrent/Avatar';
import useBrandName from 'utility/hooks/useBrandName';
import useBrandPolicyLinks from 'utility/hooks/useBrandPolicyLinks';
import ButtonBase from '@liquidweb-dev/undercurrent/ButtonBase';
import Link from '@liquidweb-dev/undercurrent/Link';
import ListItemIcon from '@liquidweb-dev/undercurrent/ListItemIcon';
import Menu from '@liquidweb-dev/undercurrent/Menu';
import MenuItem from '@liquidweb-dev/undercurrent/MenuItem';
import Stack from '@liquidweb-dev/undercurrent/Stack';
import Divider from '@liquidweb-dev/undercurrent/Divider';
import ListItemText from '@liquidweb-dev/undercurrent/ListItemText';
import ArrowUpRightIcon from '@liquidweb-dev/undercurrent/icons/lucide/ArrowUpRight';
import CircleDollarSignIcon from '@liquidweb-dev/undercurrent/icons/lucide/CircleDollarSign';
import Tooltip from '@liquidweb-dev/undercurrent/Tooltip';
import VenetianMaskIcon from '@liquidweb-dev/undercurrent/icons/custom/VenetianMask';
import LogOutIcon from '@liquidweb-dev/undercurrent/icons/lucide/LogOut';
import useLogout from 'modules/oidc/useLogout';
import useAuthenticated, {
	useMasquerade,
	useUsername,
} from 'modules/oidc/useAuthenticated';
import { selectors as appConfigSelectors } from 'modules/appConfig';
import useAccountUserDetails from 'modules/queries/account/user/useDetails';
import { IntroTarget, introTargets } from 'components/introjs';
import { useNavDialogsContext } from '../navDialogsContext';
import useLeftHandLayout from '../context';
import HookedLink from './HookedLink';

export function TopNavUser() {
	const isMasquerade = useMasquerade();
	const { setIsReferFriendModalOpen } = useNavDialogsContext();
	const [menuElement, setMenuElement] = useState();
	const open = menuElement !== undefined;
	const isAuthed = useAuthenticated();
	const username = useUsername();

	// fetch logged in user info
	const { data: userDetails } = useAccountUserDetails(
		{ username },
		{ enabled: isAuthed },
	);
	const nameInitial = userDetails?.name[0];

	const logout = useLogout();

	const { appPath } = useLeftHandLayout();

	// Close menu when appPath changes
	useEffect(() => {
		if (appPath === undefined) return;
		setMenuElement(undefined);
	}, [appPath]);

	const userProfilePath = useSelector(appConfigSelectors.getUserProfilePath);

	const brandName = useBrandName();
	const policyLinks = useBrandPolicyLinks();

	return (
		<>
			<Tooltip title="Account management">
				<IntroTarget introId={introTargets.navAccountMenu}>
					<ButtonBase
						onClick={({ currentTarget }) => setMenuElement(currentTarget)}
						sx={(theme) => ({
							borderRadius: theme.borderRadius.roundedFull,
							'&:focus-visible': {
								outlineOffset: '2px',
								outlineWidth: '2px',
								outlineStyle: 'solid',
								outlineColor: theme.vars.palette.uc.highlight.border.main,
							},
						})}
						disableRipple
					>
						<Stack
							sx={{
								position: 'relative',
							}}
						>
							<Avatar
								sx={({ vars }) => ({
									height: '40px',
									width: '40px',
									border: isMasquerade
										? `1px solid ${vars.palette.uc.highlight.border.main}`
										: 'initial',
								})}
							>
								{nameInitial}
							</Avatar>
							{isMasquerade && (
								<VenetianMaskIcon
									sx={({ size }) => ({
										position: 'absolute',
										bottom: '-2px',
										right: '-2px',
										width: size.xS,
										height: size.xS,
									})}
								/>
							)}
						</Stack>
					</ButtonBase>
				</IntroTarget>
			</Tooltip>
			<Menu
				open={open}
				anchorEl={menuElement}
				anchorOrigin={{
					vertical: 'bottom',
					horizontal: 'right',
				}}
				transformOrigin={{
					vertical: 'top',
					horizontal: 'right',
				}}
				onClose={() => setMenuElement()}
				sx={{ zIndex: { xs: 1201, sm: 1200 } }}
			>
				<MenuItem component={HookedLink} href="/account" role="menuitem">
					<ListItemText primary="Account management" />
				</MenuItem>
				<MenuItem
					component={HookedLink}
					href="/account/billing"
					role="menuitem"
				>
					<ListItemText primary="Billing center" />
				</MenuItem>
				<MenuItem component={HookedLink} href="/account/users" role="menuitem">
					<ListItemText primary="Manage users" />
				</MenuItem>
				<MenuItem
					component={Link}
					href={userProfilePath}
					target="_blank"
					role="menuitem"
				>
					<ListItemText primary="My user profile" />
					<ListItemIcon>
						<ArrowUpRightIcon />
					</ListItemIcon>
				</MenuItem>
				<MenuItem
					component={Link}
					href={policyLinks.policies}
					target="_blank"
					role="menuitem"
				>
					<ListItemText primary="Policies" />
					<ListItemIcon>
						<ArrowUpRightIcon />
					</ListItemIcon>
				</MenuItem>
				{brandName === 'Liquid Web' && (
					<>
						<Divider sx={{ marginX: -50 }} />
						<MenuItem onClick={() => setIsReferFriendModalOpen(true)}>
							<ListItemIcon>
								<CircleDollarSignIcon
									sx={{
										height: ({ size }) => size.xS,
										width: ({ size }) => size.xS,
										color: ({ vars }) => vars.palette.uc.success.icon.main,
									}}
								/>
							</ListItemIcon>
							<ListItemText
								primary="Refer a friend"
								sx={{ color: ({ vars }) => vars.palette.uc.success.text.main }}
							/>
						</MenuItem>
					</>
				)}
				<Divider sx={{ marginX: -50 }} />
				<MenuItem
					onClick={logout}
					sx={{
						':hover': {
							backgroundColor: ({ vars }) => vars.palette.uc.danger.bg.main,
						},
					}}
				>
					<ListItemIcon>
						<LogOutIcon
							sx={{
								height: ({ size }) => size.xS,
								width: ({ size }) => size.xS,
								color: ({ vars }) => vars.palette.uc.danger.icon.main,
							}}
						/>
					</ListItemIcon>
					<ListItemText
						primary="Logout"
						sx={{ color: ({ vars }) => vars.palette.uc.danger.text.main }}
					/>
				</MenuItem>
			</Menu>
		</>
	);
}

export default TopNavUser;
